import { IHttpService, IPromise, IQService } from 'angular';
import HttpService from '../../common/services/http.service';
import { ListeningPackage, UserListeningPackagesResponse, PackageThumbnail, HashRequestResponse, ListeningPackageType, PromocodeResponse, PromocodeCalculateRequest, PromocodeCalculateResponse } from '../../common/services/package/package.model';
import PackageService from '../../common/services/package/package.service';
import { Invoice } from '../../buy/services/invoice.model';
import { PaymentMethod } from '../../buy/services/payment-method.model';
import { EulaModel, MobileContractDto } from '../../common/services/user/eula.model';

/* @ngInject */
export default class NoAuthService {
  private path = "hafifmuzik/mobile/noauth/subscribe/";
  private masterpassVersion: string;
  private masterpassTokens: { [msisdn: string]: string } = {};

  constructor(
    private i18n,
    private $http: IHttpService,
    private $q: IQService,
    private config,
    private httpService: HttpService,
    private packageService: PackageService
  ) {
    this.initializeMasterpassSdk();
  }

  private initializeMasterpassSdk(): void {
    const endpoint = this.config.masterpass && this.config.masterpass.sdkEndpoint;
    const masterpass = (window as any)['Masterpass'];
    if (!!masterpass && !!endpoint) {
      masterpass.setEndpoint(endpoint);
    }
  }

  public getMasterpassVersion(): IPromise<string> {
    if (!!this.masterpassVersion) {
      return this.$q.when(this.masterpassVersion);
    }

    return this.httpService.extractResponseResult(
      this.$http.get(`hafifmuzik/mobile/masterpass/version`)
    ).then(version => {
      this.masterpassVersion = version === 'v1' ? 'v1' : 'v2';
      return this.masterpassVersion;
    });
  }

  public isMasterpassV2(): IPromise<boolean> {
    return this.getMasterpassVersion().then(version => version === 'v2');
  }

  public callMasterpassV2WithFreshToken<T>(msisdn: string, action: (token: string) => IPromise<T>): IPromise<T> {
    return this.ensureToken(msisdn).then(token => this.executeWithTokenRetry<T>(msisdn, action, token));
  }

  private executeWithTokenRetry<T>(msisdn: string, action: (token: string) => IPromise<T>, token: string): IPromise<T> {
    return action(token).then(
      response => response,
      error => {
        const errorCode = this.getMasterpassExceptionCode(error);
        const isAuthError = error && (error.statusCode === 401 || error.statusCode === 403 || errorCode === 'TOKEN_HAS_EXPIRED' || errorCode === 'TOKEN_IS_NOT_VALID' || errorCode === 'AUTHENTICATION_FAILED');

        if (isAuthError) {
          return this.getTokenV2(msisdn).then(
            freshToken => {
              this.masterpassTokens[msisdn] = freshToken;
              return action(freshToken);
            },
            _tokenError => {
              return this.$q.reject(error);
            }
          );
        }
        return this.$q.reject(error);
      }
    );
  }

  private ensureToken(msisdn: string): IPromise<string> {
    if (this.masterpassTokens[msisdn]) {
      return this.$q.when(this.masterpassTokens[msisdn]);
    }
    return this.getTokenV2(msisdn).then(token => {
      this.masterpassTokens[msisdn] = token;
      return token;
    });
  }

  public masterpassAccountAccess(msisdn: string, accountKey: string, userId?: string): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        const request: any = {
          accountKey: accountKey,
          accountKeyType: 'Msisdn'
        };
        if (!!userId) {
          request.userId = userId;
        }

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.accountAccess(request, callback);
        }).then((response: any) => {
          if (response && response.result && response.result.cards) {
            response.result.cards = this.toV1MasterpassCards(response.result.cards);
          }
          return response;
        });
      });
    });
  }

  public masterpassAddCard(msisdn: string, request: any): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        try {
          const masterpass = (window as any)['Masterpass'];
          masterpass.setToken(token);
          masterpass.setMerchantId(this.getMasterpassMerchantId());

          return this.wrapMasterpassCallback(callback => {
            masterpass.accountService.addCard({...request}, callback);
          });
        } catch (sdkError) {
          // SDK threw synchronous error - reject it so retry logic and controller can handle it
          const err = sdkError as any;
          return this.$q.reject({
            exception: {
              code: 'SDK_ERROR',
              message: err.message
            }
          });
        }
      });
    });
  }

  public masterpassVerifyOtp(msisdn: string, otpCode: string): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.verifyService.verifyOtp({
            otpCode: otpCode
          }, callback);
        });
      });
    });
  }

  public masterpassResendOtp(msisdn: string): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.verifyService.resendOtp({}, callback);
        });
      });
    });
  }

  public masterpassLinkToMerchant(msisdn: string, accountKey: string): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.linkToMerchant({
            accountKey: accountKey
          }, callback);
        });
      });
    });
  }

  public masterpassRecurringOrder(msisdn: string, request: any): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.recurringOrder(request, callback);
        });
      });
    });
  }

  public masterpassRecurringOrderDelete(msisdn: string, request: any): IPromise<any> {
    return this.callMasterpassV2WithFreshToken<any>(msisdn, token => {
      return this.$q.when(null).then(() => {
        const masterpass = window['Masterpass'];
        masterpass.setToken(token);
        masterpass.setMerchantId(this.getMasterpassMerchantId());

        return this.wrapMasterpassCallback(callback => {
          masterpass.accountService.recurringOrderDelete(request, callback);
        });
      });
    });
  }

  public getOfferIdFromType(packageType: string): IPromise<string> {
    return this.httpService.getResultFromCache(String(packageType),
      this.path + `getOfferIdFromType?type=${packageType}`);
  }

  public getPackageFromOfferId(offerId: string): IPromise<ListeningPackage> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `getPackageFromOfferId?offerId=${offerId}`, {
        headers: {
          'Accept-Language': this.i18n.getCurrentLanguage()
        }
      }));
  }

  public getRelatedCC(msisdn: string, offerId: string): IPromise<ListeningPackage> {
    return this.httpService.getResultFromCache('relatedCC_' + msisdn,
      this.path + `getRelatedCC?msisdn=${msisdn}&offerId=${offerId}`);
  }

  public sendOTP(msisdn: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `sendOTP?msisdn=${msisdn}`));
  }

  public validateOTP(msisdn: string, smsCode: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `validateOTP?msisdn=${msisdn}&smsCode=${smsCode}`));
  }

  public checkOTP(msisdn: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `checkOTP?msisdn=${msisdn}`));
  }

  public getTimeout(): IPromise<number> {
    return this.httpService.getResultFromCache("timeout", this.path + `getTimeout`);
  }

  public checkOrCreateUser(msisdn: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `checkOrCreateUser?msisdn=${msisdn}`));
  }

  public listPackages(msisdn: string): IPromise<UserListeningPackagesResponse> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `list?msisdn=${msisdn}`, {
        headers: {
          'Accept-Language': this.i18n.getCurrentLanguage()
        }
      }));
  }

  public getSubscriptionAvailability(msisdn: string): IPromise<MobileContractDto> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `getSubscriptionAvailability?msisdn=${msisdn}`));
  }

  public getEulaUrl(msisdn: string): IPromise<EulaModel> {
    return this.httpService.getResultFromCache('eulaUrl',
      this.path + `getEulaUrl?msisdn=${msisdn}`);
  }

  public isTurkcellSubscriber(msisdn: string): IPromise<boolean> {
    return this.httpService.getResultFromCache('tcellSubscriber',
      this.path + `isTurkcellSubscriber?msisdn=${msisdn}`);
  }

  public queryInvoiceInfo(msisdn: string): IPromise<Invoice> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `queryInvoiceInfo?msisdn=${msisdn}`));
  }

  public getCityList(): IPromise<any[]> {
    return this.httpService.getResultFromCache('cityList',
      this.path + `getCityList`);
  }

  public getCountyList(cityCode: number): IPromise<any[]> {
    return this.httpService.getResultFromCache('countyList',
      this.path + `getCountyList?cityCode=${cityCode}`);
  }

  public createUpdateInvoiceAddress(invoice: Invoice): IPromise<boolean> {
    invoice.address.country = this.config.defaultCountry;
    return this.httpService.extractResponseResult(
      this.$http.post(this.path + `createUpdateInvoiceAddress`, invoice));
  }

  public getCreditCardList(msisdn: string): IPromise<PaymentMethod[]> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `getCreditCardList?msisdn=${msisdn}`));
  }

  public createPaymentMethod(
    msisdn: string,
    sessionId: string,
    token: string,
    alias: string
  ): IPromise<string> {
    return this.httpService.extractResponseResult(
      this.$http.post(
        this.path + `createPaymentMethod?msisdn=${msisdn}`,
        `alias=${alias}`,
        {
          headers: {
            'X-ThreeD-Session-Id': sessionId,
            'X-Paycell-Card-Token': token,
            'content-type': 'application/x-www-form-urlencoded'
          }
        }
      )
    );
  }

  public updatePaymentMethod(msisdn: string, paymentMethodID: number, threedSessionId?: string, cardId?: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.post(this.path + `updatePaymentMethod?msisdn=${msisdn}`, {
        paymentMethodID: paymentMethodID,
        cardId: cardId
      },
        !!threedSessionId ? {
          headers: {
            'X-ThreeD-Session-Id': threedSessionId,
          }
        } : {})
    );
  }

  public paymentHashData(): IPromise<HashRequestResponse> {
    return this.httpService.extractResponseResult(
      this.$http.post(this.path + `getHashData`, {}));
  }

  public get3DSessionIDForChangePayment(msisdn: string, token: string, packet: PackageThumbnail): IPromise<string> {
    if (packet && (packet.type === ListeningPackageType.SDP || packet.type === ListeningPackageType.MOBILODEME)) {
      return this.get3DSessionID(msisdn, token, packet.id);
    } else {
      return this.get3DSessionID(msisdn, token, '');
    }
  }

  public get3DSessionIDForUpdate(msisdn: string, offerId: string, paymentMethodID: number, cardId?: string): IPromise<string> {
    return this.get3DSessionID(msisdn, undefined, offerId, paymentMethodID, cardId);
  }

  public get3DSessionID(msisdn: string, token: string, offerId: string, paymentMethodID?: number, cardId?: string): IPromise<string> {
    return this.httpService.extractResponseResult(
      this.$http.post(
        this.path + `get3DSessionID?msisdn=${msisdn}&offerId=${offerId}`,
        { installmentCount: '0', paymentMethodId: paymentMethodID, cardId: cardId },
        {
          headers: {
            'X-Paycell-Card-Token': token
          }
        }
      )
    );
  }

  public get3DSessionResult(msisdn: string, sessionId: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.post(
        this.path + `get3DSessionResult?msisdn=${msisdn}`, {},
        {
          headers: {
            'X-ThreeD-Session-Id': sessionId
          }
        }
      )
    );
  }

  public getContractByKey(msisdn: string, key: string, offerId?: string): IPromise<string> {
    let url = this.path + `getContract?msisdn=${msisdn}&key=${key}`;
    let cacheKey = `getContract${key}`;
    if (!angular.isUndefined(offerId)) {
      url += `&offerId=${offerId}`;
      cacheKey += `&offerId=${offerId}`;
    }
    return this.httpService.getResultFromCache(cacheKey, url);
  }

  public getToken(msisdn: string): IPromise<string> {
    return this.getMasterpassVersion().then(version => {
      if (version === 'v2') {
        return this.getTokenV2(msisdn);
      }

      return this.getTokenV1(msisdn);
    });
  }

  private getTokenV1(msisdn: string): IPromise<string> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `token?msisdn=${msisdn}`));
  }

  private getTokenV2(msisdn: string): IPromise<string> {
    return this.getTokenV1(msisdn);
  }

  public updateSubscriptionMethod(cpcmOfferId: string, cardName: string, cardUniqueId: string): IPromise<boolean> {
    return this.httpService.extractResponseResult(
      this.$http.post(this.path + `updateSubscriptionPayment`, {
        cpcmOfferId: cpcmOfferId,
        cardName: cardName,
        cardUniqueId: cardUniqueId
      }), true, true);
  }

  public startSubscription(
    msisdn: string,
    packet: PackageThumbnail,
    cardName: string,
    cardUniqueId: string,
    promocode: string
  ): IPromise<Response> {
    if (!promocode) {
      return this.httpService.extractResponseResult(
        this.$http.post(
          this.path + `startSubscription?msisdn=${msisdn}`,
          {
            offerId: packet.id,
            cardName: cardName,
            cardUniqueId: cardUniqueId
          }
        ), true, true
      );
    }
    return this.httpService.extractResponseResult(
      this.$http.post(
        this.path + `startSubscription?msisdn=${msisdn}&promocode=${promocode}`,
        {
          offerId: packet.id,
          cardName: cardName,
          cardUniqueId: cardUniqueId
        }
      ), true, true
    );
  }

  public checkPromocode(msisdn: string, promocode: string, sardisOfferId: string): IPromise<PromocodeResponse> {
    return this.httpService.extractResponseResult(
      this.$http.get(this.path + `checkPromocode?msisdn=${msisdn}&promocode=${promocode}&sardisOfferId=${sardisOfferId}`));
  }

  /**
   * OTP ile dogrulanmis msisdn icin promokod hesaplama.
   * Girisli akistaki listeningpackage/v2/checkPromocode ile ayni govdeyi kullanir;
   * msisdn query param olarak gider. territory backend'de "TR" varsayilani ile
   * isaretli oldugu icin gonderilmez.
   */
  public calculatePromocode(
    msisdn: string,
    promoCode: string,
    offerId: string
  ): IPromise<PromocodeCalculateResponse> {
    const request: PromocodeCalculateRequest = {
      offerId,
      promoCode
    };

    return this.httpService.extractResponseResult(
      this.$http.post(this.path + `v2/checkPromocode?msisdn=${msisdn}`, request)
    );
  }

  public convertListeningPackage(listeningPackage: ListeningPackage): PackageThumbnail {
    return this.packageService.convertListeningPackage(listeningPackage);
  }
  private isMasterpassTokenExpiredError(error: any): boolean {
    return this.getMasterpassExceptionCode(error) === 'TOKEN_HAS_EXPIRED';
  }

  private getMasterpassExceptionCode(error: any): string {
    if (!error) {
      return null;
    }

    if (error.exception && error.exception.code) {
      return error.exception.code;
    }

    if (error.data && error.data.exception && error.data.exception.code) {
      return error.data.exception.code;
    }

    if (error.result && error.result.responseCode) {
      return error.result.responseCode;
    }

    if (error.data && error.data.result && error.data.result.responseCode) {
      return error.data.result.responseCode;
    }

    // Check for 401/403 HTTP status codes which indicate authentication failures
    if (error.statusCode === 401 || error.statusCode === 403) {
      return 'AUTHENTICATION_FAILED';
    }

    return null;
  }

  public toV1MasterpassCards(cards: any[]): any[] {
    if (!cards) {
      return [];
    }

    return cards.map(card => {
      return {
        BankIca: card.cardIssuerIcaNumber,
        CardStatus: card.cardState,
        EftCode: card.eftCode,
        IsMasterPassMember: card.isMasterpassMember,
        LoyaltyCode: null,
        Name: card.cardAlias,
        ProductName: card.productName,
        PromtCpin: null,
        UniqueId: card.uniqueCardNumber,
        Value1: card.maskedCardNumber,
        Value2: null,
        userListeningPackages: []
      };
    });
  }


  private getMasterpassMerchantId(): string {
    return this.config.masterpass.merchantId || this.config.masterpass.clientId;
  }

  private wrapMasterpassCallback<T>(action: (callback: any) => void): IPromise<T> {
    const deferred = this.$q.defer<T>();
    const self = this;
    action(function(statusCode: any, responseBody: any) {
      const response = responseBody !== undefined ? responseBody : statusCode;
      const errorCode = self.getMasterpassExceptionCode(response);

      if (errorCode === 'TOKEN_HAS_EXPIRED' || errorCode === 'TOKEN_IS_NOT_VALID' || errorCode === 'AUTHENTICATION_FAILED') {
        deferred.reject(response);
        return;
      }

      deferred.resolve(response);
    });
    return deferred.promise;
  }
}



// WEBPACK FOOTER //
// ./src/app/no-auth/services/noauth.service.ts